import { useState } from 'react';
import type { CalendarStripItem, CardDto, Destination, PreviewDto } from '@konvert/shared';
import {
  addDays,
  addMonths,
  formatDateWithWeekdayRu,
  formatMinutesRu,
  localDateOf,
  startOfMonth,
  startOfWeek,
} from '@konvert/domain';
import { Sheet } from './Sheet.js';
import { t } from '../lib/i18n.js';

export interface LimitPrompt {
  message: string;
  preview?: PreviewDto;
  /** Re-runs the same operation with the override the user just confirmed. */
  retry: (reason: string) => void;
}

/** Move dialog with the load of each day and the explicit override (SPEC 11.2). */
export function MoveDialog({
  card,
  strip,
  today,
  onClose,
  onMove,
}: {
  card: CardDto | null;
  strip: CalendarStripItem[];
  today: string;
  onClose: () => void;
  onMove: (destination: Destination) => void;
}): JSX.Element {
  const options: Array<{ label: string; destination: Destination }> = [
    { label: t('move.today'), destination: { kind: 'day', date: today } },
    { label: t('move.tomorrow'), destination: { kind: 'day', date: addDays(today, 1) } },
    { label: t('move.week'), destination: { kind: 'week', date: startOfWeek(today, 1) } },
    {
      label: t('move.next_week'),
      destination: { kind: 'week', date: addDays(startOfWeek(today, 1), 7) },
    },
    { label: t('move.month'), destination: { kind: 'month', date: startOfMonth(today) } },
    {
      label: t('move.next_month'),
      destination: { kind: 'month', date: startOfMonth(addMonths(today, 1)) },
    },
    { label: t('move.inbox'), destination: { kind: 'inbox' } },
    { label: t('move.someday'), destination: { kind: 'someday' } },
  ];

  const loadOf = (date: string): string => {
    const item = strip.find((s) => s.date === date);
    if (!item) return '';
    return `${formatMinutesRu(item.time_load_minutes)} ${t('load.of')} ${formatMinutesRu(item.max_minutes)}`;
  };

  return (
    <Sheet open={card !== null} onClose={onClose} title={t('move.title')}>
      <div className="space-y-2">
        {options.map((option) => (
          <button
            key={option.label}
            type="button"
            onClick={() => onMove(option.destination)}
            className="paper-card flex w-full items-center justify-between px-3 py-3 text-left"
          >
            <span className="text-ink">{option.label}</span>
            <span className="text-xs text-ink-soft">
              {option.destination.kind === 'day' ? loadOf(option.destination.date) : ''}
            </span>
          </button>
        ))}
        <div className="pt-2">
          <p className="mb-2 text-xs uppercase tracking-wide text-ink-soft">{t('index.days')}</p>
          <div className="grid grid-cols-2 gap-2">
            {strip.slice(0, 10).map((item) => (
              <button
                key={item.date}
                type="button"
                onClick={() => onMove({ kind: 'day', date: item.date })}
                className="paper-card px-3 py-2 text-left"
              >
                <span className="block text-sm text-ink">{formatDateWithWeekdayRu(item.date)}</span>
                <span className={`block text-xs ${item.exceeded ? 'text-brick' : 'text-ink-soft'}`}>
                  {formatMinutesRu(item.time_load_minutes)} {t('load.of')}{' '}
                  {formatMinutesRu(item.max_minutes)}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Sheet>
  );
}

/** Confirmation screen for exceeding the limit (SPEC 5.1, 11.2). */
export function LimitConfirm({
  prompt,
  onCancel,
  onConfirm,
}: {
  prompt: LimitPrompt | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): JSX.Element {
  const [reason, setReason] = useState('');
  const day = prompt?.preview?.affected_days[0];

  return (
    <Sheet open={prompt !== null} onClose={onCancel} title={t('load.exceeded')}>
      <p className="mb-3 text-sm text-ink">{prompt?.message}</p>
      {day ? (
        <div className="paper-card mb-3 px-3 py-2 text-sm text-ink-soft">
          <div>
            Было: {formatMinutesRu(day.load_before.time_load_minutes)}, энергия{' '}
            {day.load_before.energy_points}
          </div>
          <div>
            Станет: {formatMinutesRu(day.load_after.time_load_minutes)}, энергия{' '}
            {day.load_after.energy_points}
          </div>
          <div>
            Лимит: {formatMinutesRu(day.load_after.max_minutes)}, энергия{' '}
            {day.load_after.max_energy_points}
          </div>
        </div>
      ) : null}
      <input
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder={t('move.reason')}
        className="mb-3 w-full rounded-[var(--radius-card)] border border-[var(--line)] bg-paper px-3 py-2 text-sm text-ink outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-2.5 text-ink"
        >
          {t('move.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onConfirm(reason)}
          className="flex-1 rounded-[var(--radius-card)] bg-[var(--ink)] px-3 py-2.5 text-[var(--paper)]"
        >
          {t('move.confirm')}
        </button>
      </div>
    </Sheet>
  );
}

export function todayOf(timezone: string): string {
  return localDateOf(new Date(), timezone);
}
